import { pickBy } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import Select from 'react-select';
import { ValueType } from 'react-select/src/types';
import { Form, FormGroup } from 'reactstrap';
import { autoinject, inject } from '../Utils/AutoInject';
import { bind } from '../Utils/Bind';
import { errorRedirect } from '../Utils/ErrorHandle';
import { FormStore } from '../Utils/FormStore';
import { IFormField } from '../Utils/PrimitiveFormField';
import { TasksStore } from '../Utils/TasksStore';
import { TicketsStore } from '../Utils/TicketsStore';
import { AlertHandle } from './Alert';
import { Input } from './Input';
import { SelectInput } from './SelectInput';

export interface ITicketFormProps {
    onSubmit(model: any): void;
    onFormChange?(): void;
    readonly isPortal?: boolean;
    readonly alertHandle: AlertHandle;
    readonly ticketForm: FormStore<any>;
}

@autoinject
@observer
export class TicketForm extends React.Component<ITicketFormProps> {
    @action
    public async componentDidMount() {
        this.loading = true;
        try {
            await this.ticketsStore.loadCreateMetaAsync();
        } catch (e: any) {
            errorRedirect(e);
        } finally {
            this.loading = false;
        }

        this.pruneFormFields();
    }

    public render() {
        return (
            <div className="vh-50">
                {!this.loading && this.props.ticketForm && this.ticketsStore.createMeta && (
                    <div className="p-3">
                        {!!this.props.ticketForm.globalErrors.length && (
                            <div className="alert-danger p-2">
                                <ul>
                                    {this.props.ticketForm.globalErrors.map((e: string, idx: number) => (
                                        <li key={idx}>{e}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <Form onSubmit={this.onSubmit}>
                            {!this.props.isPortal && (
                                <FormGroup color={this.props.ticketForm.fields.ticket_form_id.state}>
                                    <label>Form</label>
                                    <Input
                                        type="select"
                                        field={this.props.ticketForm.fields.ticket_form_id}
                                        onChange={this.onFormChange}
                                        required
                                    >
                                        <option value="" disabled>
                                            -- select an option --
                                        </option>
                                        {this.ticketsStore.createMeta!.forms.map(form => (
                                            <option key={form.id} value={form.id}>
                                                {form.name}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            )}

                            <FormGroup
                                color={this.props.ticketForm.fields[this.ticketsStore.formField('Subject')].state}
                            >
                                <label>Subject</label>
                                <Input
                                    field={this.props.ticketForm.fields[this.ticketsStore.formField('Subject')]}
                                    type="text"
                                    required
                                />
                            </FormGroup>

                            {!this.props.isPortal && (
                                <FormGroup color={this.props.ticketForm.fields.internal_note.state}>
                                    <label>Internal Note</label>
                                    <Input
                                        field={this.props.ticketForm.fields.internal_note}
                                        type="textarea"
                                        required
                                    />
                                </FormGroup>
                            )}

                            {this.props.isPortal && (
                                <FormGroup
                                    color={
                                        this.props.ticketForm.fields[this.ticketsStore.formField('Description')].state
                                    }
                                >
                                    <label>Description</label>
                                    <Input
                                        field={this.props.ticketForm.fields[this.ticketsStore.formField('Description')]}
                                        type="textarea"
                                        required
                                    />
                                </FormGroup>
                            )}

                            {!this.props.isPortal && (
                                <FormGroup>
                                    <label>Tags</label>

                                    <SelectInput
                                        className="react-select-container"
                                        options={this.tagOptions}
                                        isMulti
                                        field={this.props.ticketForm.fields.tags}
                                    />
                                </FormGroup>
                            )}

                            {this.selectedFormFields?.filter(this.isApplicable).map(this.renderDynamicField)}

                            <FormGroup className="text-center mb-0">
                                <button type="submit" className="btn btn-primary">
                                    Submit
                                </button>
                            </FormGroup>
                        </Form>
                    </div>
                )}
            </div>
        );
    }

    @bind
    private renderDynamicField(field: any) {
        //const key = field.schema.customId ? 'customfield_' + field.schema.customId : field.schema.system;
        const formField: IFormField<any> = this.props.ticketForm.fields[field.id];

        if (!formField) {
            return <div>Error...</div>;
        }

        let editable = true;
        let required = false;

        if (this.props.isPortal) {
            editable = field.editable_in_portal;
            required = field.required_in_portal;
        }

        return (
            <FormGroup key={field.id} color={formField.state}>
                <label>{field.title}</label>
                {field.type == 'text' && (
                    <Input field={formField} type="text" required={required} disabled={!editable} />
                )}
                {field.type == 'textarea' && (
                    <Input field={formField} type="textarea" required={required} disabled={!editable} />
                )}
                {field.type == 'decimal' && (
                    <Input field={formField} type="number" required={required} disabled={!editable} />
                )}
                {field.type == 'integer' && (
                    <Input field={formField} type="number" step={1} required={required} disabled={!editable} />
                )}
                {field.type == 'date' && (
                    <Input field={formField} type="date" required={required} disabled={!editable} />
                )}
                {field.type == 'checkbox' && (
                    <Input
                        field={formField}
                        type="checkbox"
                        required={required}
                        disabled={!editable}
                        className="ms-3"
                    />
                )}
                {(field.type == 'tagger' || field.type == 'tickettype' || field.type == 'priority') && (
                    <Input field={formField} type="select" required={required} disabled={!editable}>
                        <option value="" disabled>
                            -
                        </option>
                        {field.system_field_options &&
                            field.system_field_options.map((o: any) => (
                                <option key={o.value} value={o.value}>
                                    {o.name}
                                </option>
                            ))}
                        {field.custom_field_options &&
                            field.custom_field_options.map((o: any) => (
                                <option key={o.id} value={o.value}>
                                    {o.name}
                                </option>
                            ))}
                    </Input>
                )}
                {field.type == 'multiselect' && (
                    <Input field={formField} type="select" multiple required={required} disabled={!editable}>
                        <option value="" disabled>
                            -
                        </option>
                        {field.system_field_options &&
                            field.system_field_options.map((o: any) => (
                                <option key={o.value} value={o.value}>
                                    {o.name}
                                </option>
                            ))}
                        {field.custom_field_options &&
                            field.custom_field_options.map((o: any) => (
                                <option key={o.id} value={o.value}>
                                    {o.name}
                                </option>
                            ))}
                    </Input>
                )}
                {field.type == 'regexp' && <div>Error! regexp field type not implemented</div>}
            </FormGroup>
        );
    }

    @bind
    private isApplicable(field: any) {
        if (this.props.isPortal) {
            if (
                this.ticketsStore.formField('Priority') != null &&
                field.id == this.ticketsStore.formField('Priority')
            ) {
                return false;
            }

            return field.active && field.visible_in_portal;
        } else {
            return field.active;
        }
    }

    @action
    private pruneFormFields() {
        if (!this.props.ticketForm || !this.selectedFormFields) return;

        // Remove all keys
        Object.keys(this.props.ticketForm.fields)
            .map(parseInt)
            .filter(id => !isNaN(id))
            .map(id => this.selectedFormFields![id])
            .filter(f => !!f?.removable)
            .forEach(f => this.props.ticketForm.remove(f.id));

        if (this.selectedFormFields) {
            const values: any = {};
            this.selectedFormFields.forEach(f => (values[f.id] = null));

            this.props.ticketForm.apply(values);
        }
    }

    @bind
    private onFormChange() {
        this.pruneFormFields();
        if (this.props.onFormChange) this.props.onFormChange();
    }

    @computed
    private get selectedFormMeta(): any {
        if (
            !this.props.ticketForm.fields.ticket_form_id ||
            !this.props.ticketForm.fields.ticket_form_id.value ||
            this.loading ||
            !this.ticketsStore.createMeta
        )
            return;

        return this.ticketsStore.createMeta!.forms.find(
            form => form.id == this.props.ticketForm.fields.ticket_form_id.value
        );
    }

    @computed
    private get selectedFormFields() {
        if (!this.selectedFormMeta) return;
        const ids = this.selectedFormMeta.ticket_field_ids as number[];

        const fieldsToSkip = [
            'Subject',
            'Description',
            'Status',
            'Group',
            'Assignee',
            'Display Serial Number',
            'Account Name',
            'Contract Number (deprecated)',
            'Contract Number',
            'Service Cost CO',
            'Contract Type',
            'Warranty Status',
            'Service Level',
            'Add-On Services',
            'Age of Display at Ticket Creation (in days)',
            'Top Level Part Number of Display',
            'Unit Serial Number-for Double Sided Units',
            'Down-time Incidents',
            'Downtime Incident Types',
            'Location',
            'Country',
            'Customer Site ID',
        ];

        return ids.map(id => this.ticketsStore.createMeta!.fields[id]).filter(f => !fieldsToSkip.includes(f.raw_title));
    }

    @bind
    private onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        this.props.ticketForm.onSubmit();
        if (this.props.ticketForm.validate()) {
            const task = this.tasksStore.startSessionTask(`Submit Ticket`);

            try {
                // Pick only fields which exist
                const fields = pickBy(this.props.ticketForm.values, f => f);
                this.props.onSubmit(fields);
                task.complete();
            } catch (e: any) {
                if (e.modelState) {
                    this.props.ticketForm.clearGlobalErrors();
                    this.props.ticketForm.addErrors(e.modelState);
                }

                task.error(e);
            }
        } else {
            console.log('invalid');
        }
    }

    @computed
    private get tagOptions(): { value: string; label: string }[] {
        if (!this.ticketsStore.createMeta) return [];

        return this.ticketsStore.createMeta!.tags.map(t => {
            return {
                value: t,
                label: t,
            };
        });
    }

    @observable
    private loading = false;
    @inject
    private ticketsStore!: TicketsStore;
    @inject
    private tasksStore!: TasksStore;
}
